import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import DependingObjects from '../../components/psc/lib/DependingObjects'
import CancelButton from '../../components/psc/lib/CancelButton'
import SaveButton from '../../components/psc/lib/SaveButton'
import DeleteButton from '../../components/psc/lib/DeleteButton'
import ErrorMessage from '../../components/psc/lib/ErrorMessage'
import SingleSelect from '../../components/psc/lib/SingleSelect'
import useCadmObject from '../../hooks/useCadmObject'
import useBrowserTabTitle from '../../hooks/useBrowserTabTitle'

const ReferenceStructure = () => {
    let { objID } = useParams()
    const isNew = objID === "new" ? true : false
    objID = parseInt(objID,10)
    const objectUrl = '/v0/reference_structures' + (isNew ? '' : '/' + objID)

    const {
        cadmObjects,
    } = useCadmObject()

    // Fields
    const [name, setName] = useState('')

    useBrowserTabTitle(isNew ? 'New Reference Structure' : (name || 'Reference Structure'))
    const [cadmObjectID, setCadmObjectID] = useState(0)
    const [structure, setStructure] = useState('')

    // Read-only
    const [isUsed, setIsUsed] = useState(false)
    const [dependingObjects, setDependingObjects] = useState([])
    const [canEditStructure, setCanEditStructure] = useState(true)
    
    // Submit
    const [submitActive, setSubmitActive] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        if ( ! isNew) {
            fetchObject()
        }
    }, [isNew])

    const fetchObject = () => {
        fetch(objectUrl, { credentials: 'same-origin' })
            .then(function(response) {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(response.statusText)
                }
            })
            .then(function(json) {
                if(json.error) {
                    throw new Error(json.error)
                }

                fromBackend(json.result)
            })
            .catch(function(error) {
                console.log(error)
            })
    }

    const fromBackend = (obj) => {
        setName(obj.name)
        setCadmObjectID(obj.cadm_object_id)
        setStructure(obj.structure)
        setIsUsed(obj.is_used)
        setDependingObjects(obj.depending_objects)
        setCanEditStructure(obj.can_edit_structure)
    }

    const toBackend = () => {
        return {
            name: name.trim(),
            cadm_object_id: cadmObjectID,
            structure: structure.trim(),
        }
    }

    const validateBeforeSubmit = () => {
        let errors = []

        if ( ! name ) {
            errors.push('Name is required')
        }

        if ( ! cadmObjectID ) {
            errors.push('Configured Item is required')
        }

        if ( ! structure ) {
            errors.push('Structure is required')
        }

        if ( errors.length > 0 ) {
            setError(errors)
            return false
        }

        return true
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setError([ props.message ])
    }
    
    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        if( submitActive || !validateBeforeSubmit() ) {
            return null
        }

        setSubmitActive(true)

        return {
            objType: 'reference_structures',
            objID: objID,
            data: toBackend(),
        }
    }

    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Reference Structure: {name || '<New Reference Structure>'}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Name
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required 
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Configured Item
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <SingleSelect
                                            value={cadmObjectID}
                                            onChange={value => setCadmObjectID(value.id)} 
                                            options={cadmObjects.filter(item => item.do_use_reference_structure )}
                                            disabled={isUsed}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label"></label>
                                    <div className="col-sm-8">
                                        <p>
                                            Available variables to put in the structure
                                        </p>
                                        <ul className="unstyled">
                                            <li>$ProjectCode</li>
                                            <li>$DocumentType</li>
                                            <li>$RecordType</li>
                                            <li>$OrgCode</li>
                                            <li>$ElementCode</li>
                                            <li>$DocumentNumber</li>
                                            <li>$RecordNumber</li>
                                            <li>$DocumentTypecodeNumber</li>
                                            <li>$RecordTypecodeNumber</li>
                                            <li>$YYYY</li>
                                            <li>$YY</li>
                                            <li>$Issue</li>
                                            <li>$Revision</li>
                                        </ul>
                                        <p>
                                            Example structure: "$ProjectCode-$DocumentType-$OrgCode-$ElementCode-$DocumentNumber $Issue"
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Structure
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                               value={structure}
                                               onChange={(e) => setStructure(e.target.value)}
                                               disabled={!canEditStructure}
                                               required 
                                        />
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />
                                        
                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={true}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />

                                        <DependingObjects list={dependingObjects} />

                                        <DeleteButton
                                           isHidden={isNew || isUsed}
                                           url={objectUrl}
                                           onError={handleSubmitError}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default ReferenceStructure
